import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useOutletContext, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getData } from "../../../webService/webservice.js";
import { useStore } from "../../../Context/context.jsx";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import { getDateRange } from "../../../utils/get-DateRange.js";
import "../../../styles/Auth/sales-report.css";

const isCashWalletBill = (item) => {
    if (!item) return false;
    if (Number(item.cashWalletDeduction || 0) > 0) return true;
    if (Array.isArray(item.paymentMode) && item.paymentMode.some(p => (p.method === "WALLET" || p.method === "CASH_WALLET") && Number(p.amount || 0) > 0)) return true;
    if (Array.isArray(item.appliedWallets) && item.appliedWallets.some(w => String(w.type || w.walletType).toLowerCase() === "cash" && Number(w.amountUsed || w.amountDeducted || 0) > 0)) return true;
    return false;
};

const getCashWalletAmount = (item) => {
    if (!item) return 0;
    if (Number(item.cashWalletDeduction || 0) > 0) return Number(item.cashWalletDeduction);
    if (Array.isArray(item.paymentMode)) {
        const walletPmt = item.paymentMode.find(p => (p.method === "WALLET" || p.method === "CASH_WALLET"));
        if (walletPmt) return Number(walletPmt.amount || 0);
    }
    if (Array.isArray(item.appliedWallets)) {
        const walletItem = item.appliedWallets.find(w => String(w.type || w.walletType).toLowerCase() === "cash");
        if (walletItem) return Number(walletItem.amountUsed || walletItem.amountDeducted || 0);
    }
    return 0;
};

const isServiceWalletBill = (item) => {
    if (!item) return false;
    if (Number(item.serviceWalletCoveredAmount || 0) > 0) return true;
    if (Array.isArray(item.items) && item.items.some(i => i.isServiceWalletApplied || i.customerWalletId || i.serviceWalletName)) return true;
    if (Array.isArray(item.appliedWallets) && item.appliedWallets.some(w => String(w.type || w.walletType).toLowerCase() === "service")) return true;
    return false;
};

const mapQueryToDateRangeLabel = (paramRange) => {
    if (!paramRange) return null;
    const lower = paramRange.toLowerCase();
    if (lower === 'today') return 'Today';
    if (lower === 'last7days' || lower === 'weekly' || lower === 'week' || lower === 'last_7_days') return 'Last 7 Days';
    if (lower === 'currentmonth' || lower === 'monthly' || lower === 'month' || lower === 'current_month') return 'Current Month';
    if (lower === 'currentfy' || lower === 'yearly' || lower === 'year' || lower === 'current_fy') return 'Current FY';
    if (lower === 'custom') return 'Custom';
    return paramRange;
};

const SalesReport = () => {
    const location = useLocation();
    const today = new Date().toISOString().split("T")[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [dateRange, setDateRange] = useState("Today");

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const paramDateRange = queryParams.get("dateRange");
        const paramFromDate = queryParams.get("fromDate");
        const paramToDate = queryParams.get("toDate");

        if (paramDateRange) {
            const mappedRange = mapQueryToDateRangeLabel(paramDateRange);
            if (mappedRange === "Custom") {
                setDateRange("Custom");
                if (paramFromDate && paramToDate) {
                    setStartDate(paramFromDate);
                    setEndDate(paramToDate);
                }
            } else if (mappedRange) {
                setDateRange(mappedRange);
                const { startDate: s, endDate: eD } = getDateRange(mappedRange);
                setStartDate(s);
                setEndDate(eD);
            }
        }
    }, [location.search]);

    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");

    // --- State Management ---
    const [activeTab, setActiveTab] = useState("bill-wise");
    const [loading, setLoading] = useState(false);

    // View Modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewInvoiceData, setViewInvoiceData] = useState(null);
    const [viewAggData, setViewAggData] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        averageBillValue: 0
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [filters, setFilters] = useState({
        staffId: "",
        paymentMode: "",
        customerId: "",
        billNo: "",
        minAmount: "",
        maxAmount: "",
        storeId: selectedStoreId || "",
        serviceCategoryId: ""
    });

    // Table Controls
    const [tableSearch, setTableSearch] = useState("");
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({
        invoiceNo: true,
        invoiceDate: true,
        clientName: true,
        staffName: true,
        services: true,
        products: true,
        memberships: true,
        discount: true,
        tax: true,
        paymentMode: true,
        totalAmount: true,
        categoryName: true,
        action: true
    });

    const toggleColumn = (col) => {
        setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
    };

    const TAB_COLUMNS = {
        "bill-wise": ["invoiceNo", "invoiceDate", "clientName", "staffName", "services", "products", "memberships", "discount", "tax", "paymentMode", "totalAmount", "action"],
        "service-wise": ["clientName", "categoryName", "services", "totalAmount", "action"],
        "product-wise": ["clientName", "services", "totalAmount", "action"],
        "membership-wise": ["clientName", "services", "totalAmount", "action"]
    };

    const COLUMN_LABELS = {
        invoiceNo: "Bill No.",
        invoiceDate: "Date & Time",
        clientName: activeTab === "bill-wise" ? "Customer Name" :
            activeTab === "service-wise" ? "Service Name" :
                activeTab === "product-wise" ? "Product Name" :
                    activeTab === "membership-wise" ? "Membership Name" : "Item Name",
        staffName: "Staff Name",
        services: activeTab === "bill-wise" ? "Services" :
            activeTab === "membership-wise" ? "Sold Count" : "Total Quantity",
        products: "Products",
        memberships: "Memberships",
        discount: "Discount",
        tax: "Tax",
        paymentMode: "Payment Mode",
        totalAmount: "Total Amount",
        categoryName: "Category Name",
        action: "Action"
    };

    const columnConfigRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnConfigRef.current && !columnConfigRef.current.contains(event.target)) {
                setShowColumnConfig(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Dropdown Lists
    const [storeList, setStoreList] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [serviceCategoryList, setServiceCategoryList] = useState([]);

    // --- Helper for Safe Array Extraction ---
    const safeExtractArray = (res) => {
        if (!res || !res.data) return [];

        // Some return [{ paginatedResults: [...] }]
        if (Array.isArray(res.data) && res.data[0]?.paginatedResults) {
            return res.data[0].paginatedResults;
        }

        // Some APIs return directly in data
        if (Array.isArray(res.data)) return res.data;

        // Some return { results: [...] } or { results: [{ paginatedResults: [...] }] }
        if (res.data.results) {
            if (Array.isArray(res.data.results)) {
                if (res.data.results[0]?.paginatedResults) {
                    return res.data.results[0].paginatedResults;
                }
                return res.data.results;
            }
        }

        // Fallback for objects that might just be the data itself
        if (typeof res.data === 'object' && !Array.isArray(res.data)) {
            // Try to find any array inside
            for (let key in res.data) {
                if (Array.isArray(res.data[key])) return res.data[key];
            }
        }

        return [];
    };

    // --- Fetching Data ---

    const fetchDropdownData = async (targetStoreId) => {
        try {
            const commonParams = { limit: 10000 };
            if (targetStoreId) commonParams.storeId = targetStoreId;

            const [storeRes, customerRes, sCatRes] = await Promise.all([
                getData("stores/stores", { limit: 10000 }, sessionToken), // Branches always show all
                getData("customers/customers", commonParams, sessionToken),
                getData("servicecategories/service-categories", commonParams, sessionToken)
            ]);

            setStoreList(safeExtractArray(storeRes));
            setCustomerList(safeExtractArray(customerRes));
            setServiceCategoryList(safeExtractArray(sCatRes));

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    const fetchSalesReport = async () => {
        try {
            setLoading(true);
            const params = {
                startDate,
                endDate,
                tab: activeTab,
                page: currentPage,
                limit: rowsPerPage,
                ...filters
            };

            // Priority: Filter StoreId > Selected StoreId
            if (!params.storeId && selectedStoreId) {
                params.storeId = selectedStoreId;
            }

            const response = await getData("reports/reports/detailed-sales", params, sessionToken);

            if (response?.status === 200 || response?.success) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {});

                // Update pagination from response
                if (response.data?.pagination) {
                    const { totalItems: ti, totalPages: tp } = response.data.pagination;
                    setTotalItems(ti);
                    setTotalPages(tp);
                }
            } else {
                toast.error(response?.message || "Failed to fetch sales report");
            }
        } catch (error) {
            console.error("Error fetching sales report:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdownData(filters.storeId || selectedStoreId);
    }, [filters.storeId, selectedStoreId]);

    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 300);
        return () => clearTimeout(handler);
    }, [filters]);

    useEffect(() => {
        // Reset to page 1 when dates or tabs change
        setCurrentPage(1);
        // Clear service category filter when not on service-wise tab
        if (activeTab !== "service-wise") {
            setFilters(prev => ({ ...prev, serviceCategoryId: "" }));
        }
        if (activeTab !== "bill-wise") {
            setFilters(prev => ({ ...prev, billNo: "" }));
        }
    }, [startDate, endDate, activeTab]);

    useEffect(() => {
        fetchSalesReport();
    }, [startDate, endDate, activeTab, selectedStoreId, currentPage, rowsPerPage, debouncedFilters]);

    // --- Styles for React Select ---
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            height: '44px',
            minHeight: '44px',
            background: '#f8fafc',
            borderColor: state.isFocused ? '#3c0008' : '#e2e8f0',
            borderRadius: '12px',
            padding: '0 4px',
            fontSize: '14px',
            color: '#1e293b',
            boxShadow: 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3c0008' : '#cbd5e1',
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#64748b',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 90,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            backgroundColor: state.isSelected ? '#3c0008' : state.isFocused ? '#f1f5f9' : '#fff',
            color: state.isSelected ? '#fff' : '#1e293b',
            '&:active': {
                backgroundColor: '#3c0008',
                color: '#fff',
            }
        })
    };

    // --- Handlers ---

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // --- Search Logic ---
    const filteredData = data.filter(item => {
        if (!tableSearch) return true;
        const search = tableSearch.toLowerCase();
        return (
            (item.invoiceNo && item.invoiceNo.toLowerCase().includes(search)) ||
            (item.clientName && item.clientName.toLowerCase().includes(search)) ||
            (item.staffName && item.staffName.toLowerCase().includes(search)) ||
            (item.serviceName && item.serviceName.toLowerCase().includes(search)) ||
            (item.productName && item.productName.toLowerCase().includes(search)) ||
            (item.name && item.name.toLowerCase().includes(search))
        );
    });

    // Calculate table totals for services, products, memberships, and total amount
    const getTableTotals = () => {
        let totalServices = 0;
        let totalProducts = 0;
        let totalMemberships = 0;
        let totalAmount = 0;
        let totalDiscount = 0;
        let totalTax = 0;

        if (activeTab === "bill-wise") {
            filteredData.forEach(item => {
                totalServices += Number(item.services) || 0;
                totalProducts += Number(item.products) || 0;
                totalMemberships += Number(item.memberships) || 0;
                totalAmount += Number(item.totalAmount) || 0;
                totalDiscount += Number(item.discount) || 0;
                totalTax += Number(item.tax) || 0;
            });
        } else if (activeTab === "service-wise") {
            filteredData.forEach(item => {
                totalServices += Number(item.totalQuantity) || 0;
                totalAmount += Number(item.totalAmount) || 0;
            });
        } else if (activeTab === "product-wise") {
            filteredData.forEach(item => {
                totalProducts += Number(item.totalQuantity) || 0;
                totalAmount += Number(item.totalAmount) || 0;
            });
        } else if (activeTab === "membership-wise") {
            filteredData.forEach(item => {
                totalMemberships += Number(item.count) || 0;
                totalAmount += Number(item.amount) || 0;
            });
        }

        return {
            totalServices,
            totalProducts,
            totalMemberships,
            totalAmount,
            totalDiscount,
            totalTax
        };
    };

    const totals = getTableTotals();

    const applyFilters = () => {
        fetchSalesReport();
    };

    const resetFilters = async () => {
        const todayStr = new Date().toISOString().split("T")[0];

        const resetData = {
            staffId: "",
            paymentMode: "",
            customerId: "",
            billNo: "",
            minAmount: "",
            maxAmount: "",
            storeId: selectedStoreId || "",
            serviceCategoryId: ""
        };

        setStartDate(todayStr);
        setEndDate(todayStr);
        setDateRange("Today");
        setFilters(resetData);
        setCurrentPage(1);
        setTableSearch("");

        try {
            setLoading(true);

            const params = {
                startDate: todayStr,
                endDate: todayStr,
                tab: activeTab,
                page: 1,
                limit: rowsPerPage,
                ...resetData
            };

            const response = await getData(
                "reports/reports/detailed-sales",
                params,
                sessionToken
            );

            if (response?.status === 200 || response?.success) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {});
                setTotalItems(response.data?.pagination?.totalItems || 0);
                setTotalPages(response.data?.pagination?.totalPages || 1);
            }
        } catch (error) {
            console.error("Error resetting filters:", error);
            toast.error("Failed to reset filters");
        } finally {
            setLoading(false);
        }
    };

    const clearTableSearch = () => {
        setTableSearch("");
        setShowColumnConfig(false);
    };

    // --- View Invoice Handler ---
    const handleViewInvoice = async (row) => {
        setViewModalOpen(true);
        setViewInvoiceData(null);
        setViewAggData(null);

        if (activeTab === "bill-wise") {
            if (!row._id) return;
            try {
                setViewLoading(true);
                const res = await getData(`invoices/invoice/${row._id}`, {}, sessionToken);
                if (res?.data) {
                    setViewInvoiceData(res.data);
                } else {
                    // Fallback: use row data directly
                    setViewInvoiceData(row);
                }
            } catch (err) {
                console.error("Error fetching invoice details", err);
                setViewInvoiceData(row);
            } finally {
                setViewLoading(false);
            }
        } else {
            // For aggregated tabs, use row data directly
            setViewAggData(row);
        }
    };

    const closeViewModal = () => {
        setViewModalOpen(false);
        setViewInvoiceData(null);
        setViewAggData(null);
    };

    const handleExport = () => {
        if (!data.length) {
            return toast.info("No data to export");
        }

        let exportData = [];

        switch (activeTab) {

            case "bill-wise":
                exportData = data.map((item) => ({
                    "Invoice No": item.invoiceNo || "",
                    "Invoice Date": item.invoiceDate
                        ? new Date(item.invoiceDate).toLocaleDateString("en-GB")
                        : "",
                    "Customer Name": item.clientName || "",
                    "Staff Name": item.staffName || "",
                    "Services": item.services || 0,
                    "Products": item.products || 0,
                    "Memberships": item.memberships || 0,
                    "Discount": item.discount || 0,
                    "Tax": item.tax || 0,
                    "Payment Mode": (() => {
                        let parts = [];
                        if (isServiceWalletBill(item)) {
                            parts.push("Service Wallet");
                        }
                        if (isCashWalletBill(item)) {
                            const walletAmt = getCashWalletAmount(item);
                            parts.push(`Cash/Price Wallet (₹${walletAmt.toFixed(2)})`);
                        }
                        if (Array.isArray(item.paymentMode)) {
                            item.paymentMode.forEach(p => {
                                if (p.method !== "WALLET" && p.method !== "CASH_WALLET" && p.method !== "SERVICE_WALLET") {
                                    parts.push(`${p.method}${p.amount ? ` (₹${Number(p.amount).toFixed(2)})` : ""}`);
                                }
                            });
                        } else if (item.paymentMode && item.paymentMode !== "WALLET" && !isCashWalletBill(item) && !isServiceWalletBill(item)) {
                            parts.push(String(item.paymentMode));
                        }
                        return parts.join(" + ") || "Cash";
                    })(),
                    "Total Amount": item.totalAmount || 0
                }));
                break;

            case "service-wise":
                exportData = data.map((item) => ({
                    "Service Name": item.serviceName || "",
                    "Category Name": item.categoryName || "",
                    "Total Quantity": item.totalQuantity || 0,
                    "Total Revenue": item.totalAmount || 0
                }));
                break;

            case "product-wise":
                exportData = data.map((item) => ({
                    "Product Name": item.productName || "",
                    "Total Quantity": item.totalQuantity || 0,
                    "Total Revenue": item.totalAmount || 0
                }));
                break;

            case "membership-wise":
                exportData = data.map((item) => ({
                    "Membership Name": item.name || "",
                    "Sold Count": item.count || 0,
                    "Total Revenue": item.amount || 0
                }));
                break;

            default:
                exportData = data;
        }

        const fileName = `Sales_Report_${activeTab}_${startDate}_to_${endDate}`;

        exportDataToExcel(
            exportData,
            "Sales Report",
            `${fileName}.xlsx`
        );
    };

    // --- Render Helpers --

    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";

    // For non-superadmins: only show stores they are assigned to
    const filteredStoreList = isSuperAdmin
        ? storeList
        : storeList.filter(s => adminStoreIds.includes(s._id));

    // Show branch filter for super-admins OR any user who manages multiple stores
    const showBranchFilter = isSuperAdmin || filteredStoreList.length > 1;

    const formatAmount = (value) => {
        return Number(value || 0).toFixed(0);
    };

    return (
        <div className="product-layout">
            <div className="product-content">
                <main className="product-main">
                    <div className="sales-report-container">
                        <div className="sales-report-standalone-header">
                            <div className="header-left">
                                <h1 className="page-title">Sales Reports</h1>
                                <p className="page-subtitle">Track and analyze your sales performance</p>
                            </div>
                            <div className="header-right">
                                <button className="export-dropdown-btn" onClick={handleExport}>
                                    <Icon icon="mdi:export-variant" width="18" /> Export <Icon icon="mdi:chevron-down" />
                                </button>
                            </div>
                        </div>

                        {/* 2. Tabs Section */}
                        <div className="sales-report-tabs-wrapper">
                            <div className="sales-report-tabs">
                                <button
                                    className={`sales-report-tab ${activeTab === "bill-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("bill-wise")}
                                >
                                    Bill-wise Sales
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "service-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("service-wise")}
                                >
                                    Service-wise Sales
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "product-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("product-wise")}
                                >
                                    Product Sales
                                </button>
                                <button
                                    className={`sales-report-tab ${activeTab === "membership-wise" ? "active" : ""}`}
                                    onClick={() => setActiveTab("membership-wise")}
                                >
                                    Membership Sales
                                </button>
                            </div>
                        </div>

                        {/* 3. Filter Section (Above Summary) */}
                        <div className="sales-filter-bar">
                            <div className="filter-grid">
                                {/* Date Range Group */}
                                <div className={`filter-item ${dateRange === "Custom" ? "date-range-custom-active" : ""}`}>
                                    <label>Date Range</label>
                                    <div className="date-range-picker-combined">
                                        <select
                                            className="filter-select date-range-select"
                                            value={dateRange}
                                            onChange={e => {
                                                const range = e.target.value;
                                                setDateRange(range);
                                                const { startDate: s, endDate: eD } = getDateRange(range, startDate, endDate);
                                                setStartDate(s);
                                                setEndDate(eD);
                                            }}
                                        >
                                            <option value="Today">Today</option>
                                            <option value="Yesterday">Yesterday</option>
                                            <option value="Last 7 Days">Last 7 Days</option>
                                            <option value="Last Month">Last Month</option>
                                            <option value="Current Month">Current Month</option>
                                            <option value="Current FY">Current Financial Year</option>
                                            <option value="Previous FY">Previous Financial Year</option>
                                            <option value="Custom">Custom Date</option>
                                        </select>
                                        {dateRange === "Custom" ? (
                                            <div className="custom-date-inputs">
                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={startDate}
                                                    onChange={(e) => setStartDate(e.target.value)}
                                                />
                                                <span className="date-separator">to</span>
                                                <input
                                                    type="date"
                                                    className="filter-input date-input"
                                                    value={endDate}
                                                    onChange={(e) => setEndDate(e.target.value)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="date-display-box">
                                                <Icon icon="mdi:calendar-outline" width="18" />
                                                <span>{startDate === endDate ? startDate : `${startDate} - ${endDate}`}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>



                                {showBranchFilter && (
                                    <div className="filter-item">
                                        <label>Branch</label>
                                        <select name="storeId" value={filters.storeId} onChange={handleFilterChange} className="filter-select">
                                            <option value="">All Branches</option>
                                            {filteredStoreList.map(s => (
                                                <option key={s._id} value={s._id}>
                                                    {s.branchName || s.storeName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {activeTab == "bill-wise" && (
                                    <div className="filter-item">
                                        <label>Payment Mode</label>
                                        <select name="paymentMode" value={filters.paymentMode} onChange={handleFilterChange} className="filter-select">
                                            <option value="">All Modes</option>
                                            <option value="CASH">Cash</option>
                                            <option value="CARD">Card</option>
                                            <option value="UPI">UPI</option>
                                            <option value="SERVICE_WALLET">Service Wallet</option>
                                            <option value="CASH_WALLET">Cash/Price Wallet</option>
                                            <option value="OTHER">Others</option>
                                        </select>
                                    </div>
                                )}

                                {activeTab == "bill-wise" && (
                                    <div className="filter-item">
                                        <label>Customer</label>
                                        <div className="search-input-wrapper">
                                            <input
                                                type="text"
                                                name="customerId"
                                                placeholder="Search Customer"
                                                value={filters.customerId}
                                                onChange={handleFilterChange}
                                                className="filter-input"
                                            />

                                        </div>
                                    </div>
                                )}

                                {activeTab === "service-wise" && (
                                    <div className="filter-item">
                                        <label>Service Category</label>

                                        <Select
                                            name="serviceCategoryId"
                                            value={
                                                filters.serviceCategoryId
                                                    ? {
                                                        value: filters.serviceCategoryId,
                                                        label:
                                                            serviceCategoryList.find(
                                                                c => c._id === filters.serviceCategoryId
                                                            )?.serviceCategoryName || "Unknown"
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) =>
                                                handleFilterChange({
                                                    target: {
                                                        name: "serviceCategoryId",
                                                        value: selected ? selected.value : ""
                                                    }
                                                })
                                            }
                                            options={[
                                                { value: "", label: "All Categories" },
                                                ...(Array.isArray(serviceCategoryList)
                                                    ? serviceCategoryList.map(c => ({
                                                        value: c._id,
                                                        label: c.serviceCategoryName
                                                    }))
                                                    : [])
                                            ]}
                                            isClearable
                                            placeholder="Select Category..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                )}

                                {activeTab === "bill-wise" && (
                                    <div className="filter-item">
                                        <label>Bill No.</label>
                                        <input
                                            type="text"
                                            name="billNo"
                                            placeholder="Search Bill No."
                                            value={filters.billNo}
                                            onChange={handleFilterChange}
                                            className="filter-input"
                                        />
                                    </div>
                                )}

                                <div className="filter-item">
                                    <label>Min. Amount</label>
                                    <input type="number" name="minAmount" placeholder="Min Amount" value={filters.minAmount} onChange={handleFilterChange} className="filter-input" />
                                </div>

                                <div className="filter-item">
                                    <label>Max. Amount</label>
                                    <input type="number" name="maxAmount" placeholder="Max Amount" value={filters.maxAmount} onChange={handleFilterChange} className="filter-input" />
                                </div>


                            </div>
                        </div>

                        {/* 4. Summary Cards Section */}
                        <div className="sales-summary-grid">
                            <div className="sales-summary-card card-bills">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:file-document-outline" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Total Bills</span>
                                    <span className="sales-card-value">{summary.noOfBills || 0}</span>
                                </div>
                            </div>
                            <div className="sales-summary-card card-revenue">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:currency-inr" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Total Revenue</span>
                                    <span className="sales-card-value">{formatAmount(summary.totalAmount)}</span>
                                </div>
                            </div>
                            {activeTab == "bill-wise" && (
                                <div className="sales-summary-card card-discount">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:tag-outline" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Discount</span>
                                        <span className="sales-card-value">{formatAmount(summary.discount)}</span>
                                    </div>
                                </div>
                            )}
                            {activeTab == "bill-wise" && (
                                <div className="sales-summary-card card-tax">
                                    <div className="sales-card-icon">
                                        <Icon icon="mdi:percent-outline" />
                                    </div>
                                    <div className="sales-card-info">
                                        <span className="sales-card-label">Total Tax</span>
                                        <span className="sales-card-value">{formatAmount(summary.tax)}</span>
                                    </div>
                                </div>
                            )}
                            <div className="sales-summary-card card-avg">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:chart-line" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Average Bill Value</span>
                                    <span className="sales-card-value">{formatAmount(summary.averageBillValue)}</span>
                                </div>
                            </div>
                        </div>

                        {/* 5. Table Section */}
                        <div className="sales-table-container">
                            <div className="table-header-row">
                                <h3 className="table-title">
                                    {activeTab === "bill-wise" ? "Bill-wise Sales Details" :
                                        activeTab === "service-wise" ? "Service Sales Details" :
                                            activeTab === "product-wise" ? "Product Sales Details" : "Membership Sales Details"}
                                </h3>
                                <div className="table-controls">
                                    <div className="table-search-box">
                                        <input
                                            type="text"
                                            placeholder="Search in table..."
                                            value={tableSearch}
                                            onChange={(e) => setTableSearch(e.target.value)}
                                        />
                                        <Icon icon="mdi:magnify" />
                                    </div>
                                    <button className="table-control-btn" onClick={clearTableSearch}>
                                        <Icon icon="mdi:filter-remove-outline" /> Clear
                                    </button>
                                    <div className="column-config-wrapper" ref={columnConfigRef}>
                                        <button
                                            className="table-control-btn"
                                            onClick={() => setShowColumnConfig(!showColumnConfig)}
                                        >
                                            <Icon icon="mdi:view-column-outline" /> Columns
                                        </button>
                                        {showColumnConfig && (
                                            <div className="column-config-dropdown">
                                                <div className="dropdown-header">Toggle Columns</div>
                                                {(TAB_COLUMNS[activeTab] || []).map(col => (
                                                    <label key={col} className="column-config-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={visibleColumns[col]}
                                                            onChange={() => toggleColumn(col)}
                                                        />
                                                        <span>{COLUMN_LABELS[col]}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="sales-table-wrapper">
                                {loading ? (
                                    <div className="loading-state">
                                        <Icon icon="mdi:loading" className="spin" />
                                        <p>Loading sales data...</p>
                                    </div>
                                ) : data.length > 0 ? (
                                    <table className="sales-data-table">
                                        <thead>
                                            {activeTab === "bill-wise" ? (
                                                <tr>
                                                    {visibleColumns.invoiceNo && <th>Bill No.</th>}
                                                    {visibleColumns.invoiceDate && <th>Invoice Date</th>}
                                                    {visibleColumns.clientName && <th>Customer Name</th>}
                                                    {visibleColumns.staffName && <th>Staff Name</th>}
                                                    {visibleColumns.services && <th>Services</th>}
                                                    {visibleColumns.products && <th>Products</th>}
                                                    {visibleColumns.memberships && <th>Memberships</th>}
                                                    {visibleColumns.discount && <th>Discount</th>}
                                                    {visibleColumns.tax && <th>Tax</th>}
                                                    {visibleColumns.paymentMode && <th>Payment Mode</th>}
                                                    {visibleColumns.totalAmount && <th>Total Amount</th>}
                                                    {visibleColumns.action && <th>Action</th>}
                                                </tr>
                                            ) : activeTab === "membership-wise" ? (
                                                <tr>
                                                    {visibleColumns.clientName && <th>Membership Name</th>}
                                                    {visibleColumns.services && <th>Sold Count</th>}
                                                    {visibleColumns.totalAmount && <th>Total Amount</th>}
                                                    {visibleColumns.action && <th>Action</th>}
                                                </tr>
                                            ) : activeTab === "service-wise" ? (
                                                <tr>
                                                    {visibleColumns.clientName && <th>Service Name</th>}
                                                    {visibleColumns.categoryName && <th>Category Name</th>}
                                                    {visibleColumns.services && <th>Total Quantity</th>}
                                                    {visibleColumns.totalAmount && <th>Total Amount</th>}
                                                    {visibleColumns.action && <th>Action</th>}
                                                </tr>
                                            ) : (
                                                <tr>
                                                    {visibleColumns.clientName && <th>Product Name</th>}
                                                    {visibleColumns.services && <th>Total Quantity</th>}
                                                    {visibleColumns.totalAmount && <th>Total Amount</th>}
                                                    {visibleColumns.action && <th>Action</th>}
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {filteredData.map((row, idx) => (
                                                <tr key={idx}>
                                                    {activeTab === "bill-wise" ? (
                                                        <>
                                                            {visibleColumns.invoiceNo && <td className="bill-no-cell">{row.invoiceNo}</td>}
                                                            {visibleColumns.invoiceDate && <td>{new Date(row.invoiceDate).toLocaleDateString("en-GB")}</td>}
                                                            {visibleColumns.clientName && <td>{row.clientName}</td>}
                                                            {visibleColumns.staffName && <td>{row.staffName}</td>}
                                                            {visibleColumns.services && <td><span className="service-cell">{row.services || 0}</span></td>}
                                                            {visibleColumns.products && <td>{row.products || 0}</td>}
                                                            {visibleColumns.memberships && <td><span className="membership-cell">{row.memberships || 0}</span></td>}
                                                            {visibleColumns.discount && <td>{(row.discount)}</td>}
                                                            {visibleColumns.tax && <td>{(row.tax)}</td>}
                                                            {visibleColumns.paymentMode && (
                                                                <td>
                                                                    <div
                                                                        className="payment-mode-cell"
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "6px",
                                                                            flexWrap: "wrap"
                                                                        }}
                                                                    >
                                                                        {isServiceWalletBill(row) && (
                                                                            <span
                                                                                style={{
                                                                                    backgroundColor: "#f3e8ff",
                                                                                    color: "#7e22ce",
                                                                                    border: "1px solid #e9d5ff",
                                                                                    padding: "2px 8px",
                                                                                    borderRadius: "6px",
                                                                                    fontSize: "12px",
                                                                                    fontWeight: "600",
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    gap: "4px"
                                                                                }}
                                                                            >
                                                                                <Icon icon="solar:stars-bold-duotone" width="16" height="16" />
                                                                                Service Wallet
                                                                            </span>
                                                                        )}
                                                                        {isCashWalletBill(row) && (
                                                                            <span
                                                                                style={{
                                                                                    backgroundColor: "#fff7ed",
                                                                                    color: "#c2410c",
                                                                                    border: "1px solid #ffedd5",
                                                                                    padding: "2px 8px",
                                                                                    borderRadius: "6px",
                                                                                    fontSize: "12px",
                                                                                    fontWeight: "600",
                                                                                    display: "inline-flex",
                                                                                    alignItems: "center",
                                                                                    gap: "4px"
                                                                                }}
                                                                            >
                                                                                <Icon icon="solar:wallet-money-bold-duotone" width="16" height="16" />
                                                                                Cash/Price Wallet (₹{getCashWalletAmount(row).toFixed(2)})
                                                                            </span>
                                                                        )}
                                                                        {Array.isArray(row.paymentMode)
                                                                            ? row.paymentMode
                                                                                .filter(mode => {
                                                                                    const m = typeof mode === "string" ? mode : mode?.method;
                                                                                    return m !== "WALLET" && m !== "CASH_WALLET" && m !== "SERVICE_WALLET";
                                                                                })
                                                                                .map((mode, index) => {
                                                                                    const paymentType = typeof mode === "string" ? mode : mode?.method || "";
                                                                                    return (
                                                                                        <div
                                                                                            key={index}
                                                                                            style={{
                                                                                                display: "flex",
                                                                                                alignItems: "center",
                                                                                                gap: "4px"
                                                                                            }}
                                                                                        >
                                                                                            <Icon
                                                                                                width="18"
                                                                                                height="18"
                                                                                                icon={
                                                                                                    paymentType?.toUpperCase() === "UPI"
                                                                                                        ? "mdi:cellphone-nfc"
                                                                                                        : paymentType?.toUpperCase() === "CARD"
                                                                                                            ? "mdi:credit-card-outline"
                                                                                                            : paymentType?.toUpperCase() === "CASH"
                                                                                                                ? "mdi:cash"
                                                                                                                : "mdi:dots-horizontal-circle-outline"
                                                                                                }
                                                                                            />
                                                                                            <span>{paymentType}</span>
                                                                                        </div>
                                                                                    );
                                                                                })
                                                                            : (!isCashWalletBill(row) && !isServiceWalletBill(row) && (
                                                                                <span>
                                                                                    {row.paymentMode?.method ||
                                                                                        row.paymentMode ||
                                                                                        "-"}
                                                                                </span>
                                                                            ))}
                                                                    </div>
                                                                </td>
                                                            )}
                                                            {visibleColumns.totalAmount && <td className="total-amount-cell">{(row.totalAmount)}</td>}
                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        title="View Invoice Details"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : activeTab === "membership-wise" ? (
                                                        <>
                                                            {visibleColumns.clientName && <td style={{ fontWeight: 600 }}>{row.name}</td>}
                                                            {visibleColumns.services && <td>{row.count}</td>}
                                                            {visibleColumns.totalAmount && <td style={{ fontWeight: 700 }}>{(row.amount)}</td>}
                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        title="View Membership Details"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {visibleColumns.clientName && <td style={{ fontWeight: 600 }}>{row.serviceName || row.productName}</td>}
                                                            {activeTab === "service-wise" && visibleColumns.categoryName && <td>{row.categoryName || "—"}</td>}
                                                            {visibleColumns.services && <td>{row.totalQuantity}</td>}
                                                            {visibleColumns.totalAmount && <td style={{ fontWeight: 700 }}>{(row.totalAmount)}</td>}
                                                            {visibleColumns.action && (
                                                                <td>
                                                                    <button
                                                                        className="action-view-btn"
                                                                        title="View Item Details"
                                                                        onClick={() => handleViewInvoice(row)}
                                                                    >
                                                                        <Icon icon="mdi:eye-outline" />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                        {filteredData.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    {(TAB_COLUMNS[activeTab] || [])
                                                        .filter(col => visibleColumns[col])
                                                        .map((col, index) => {
                                                            const isFirst = index === 0;
                                                            if (isFirst) {
                                                                return (
                                                                    <td key={col} className="table-total-label">
                                                                        <strong>Total</strong>
                                                                    </td>
                                                                );
                                                            }

                                                            if (activeTab === "bill-wise") {
                                                                if (col === "services") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <span className="service-cell">{totals.totalServices}</span>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "products") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{totals.totalProducts}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "memberships") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <span className="membership-cell">{totals.totalMemberships}</span>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "discount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>
                                                                                {Number(totals.totalDiscount || 0).toFixed(0)}
                                                                            </strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "tax") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>
                                                                                {Number(totals.totalTax || 0).toFixed(0)}
                                                                            </strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value total-amount-cell">
                                                                            {(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "service-wise") {
                                                                if (col === "services") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{totals.totalServices}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value" style={{ fontWeight: 700 }}>
                                                                            {(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "product-wise") {
                                                                if (col === "services") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{totals.totalProducts}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value" style={{ fontWeight: 700 }}>
                                                                            {(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            } else if (activeTab === "membership-wise") {
                                                                if (col === "services") {
                                                                    return (
                                                                        <td key={col} className="table-total-value">
                                                                            <strong>{totals.totalMemberships}</strong>
                                                                        </td>
                                                                    );
                                                                }
                                                                if (col === "totalAmount") {
                                                                    return (
                                                                        <td key={col} className="table-total-value" style={{ fontWeight: 700 }}>
                                                                            {(totals.totalAmount)}
                                                                        </td>
                                                                    );
                                                                }
                                                            }

                                                            return <td key={col}></td>;
                                                        })}
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                ) : (
                                    <div className="empty-state">
                                        <Icon icon="mdi:database-off-outline" />
                                        <p>No sales records found for the selected period and filters.</p>
                                    </div>
                                )}
                            </div>

                            {/* Pagination */}
                            <div className="table-pagination-row">
                                <div className="pagination-info">
                                    Showing {data.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalItems)} of {totalItems} entries
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(1)}
                                    >
                                        <Icon icon="mdi:chevron-double-left" />
                                    </button>
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                    >
                                        <Icon icon="mdi:chevron-left" />
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                className={`pagination-number ${currentPage === pageNum ? "active" : ""}`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                    >
                                        <Icon icon="mdi:chevron-right" />
                                    </button>
                                    <button
                                        className="pagination-arrow"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => setCurrentPage(totalPages)}
                                    >
                                        <Icon icon="mdi:chevron-double-right" />
                                    </button>
                                </div>
                                <div className="rows-per-page">
                                    <span>Rows per page:</span>
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* ===== View Invoice Modal ===== */}
            {viewModalOpen && (
                <div className="sr-modal-overlay" onClick={closeViewModal}>
                    <div className="sr-modal-panel" onClick={e => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="sr-modal-header">
                            <div className="sr-modal-header-left">
                                <Icon icon="mdi:file-document-outline" className="sr-modal-header-icon" />
                                <div>
                                    {activeTab === "bill-wise" ? (
                                        <>
                                            <h2 className="sr-modal-title">Invoice Details</h2>
                                            <p className="sr-modal-subtitle">
                                                {viewLoading ? "Loading..." : (viewInvoiceData?.invoiceNo || "—")}
                                            </p>
                                        </>
                                    ) : activeTab === "membership-wise" ? (
                                        <>
                                            <h2 className="sr-modal-title">Membership Details</h2>
                                            <p className="sr-modal-subtitle">{viewAggData?.name || "—"}</p>
                                        </>
                                    ) : (
                                        <>
                                            <h2 className="sr-modal-title">
                                                {activeTab === "service-wise" ? "Service Details" : "Product Details"}
                                            </h2>
                                            <p className="sr-modal-subtitle">
                                                {viewAggData?.serviceName || viewAggData?.productName || "—"}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                            <button className="sr-modal-close" onClick={closeViewModal}>
                                <Icon icon="mdi:close" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="sr-modal-body">

                            {/* BILL-WISE: full invoice detail */}
                            {activeTab === "bill-wise" && (
                                viewLoading ? (
                                    <div className="sr-modal-loading">
                                        <Icon icon="mdi:loading" className="spin" />
                                        <p>Fetching invoice details…</p>
                                    </div>
                                ) : viewInvoiceData ? (
                                    <>
                                        {isCashWalletBill(viewInvoiceData) && (
                                            <div style={{
                                                backgroundColor: '#fff7ed',
                                                border: '1px solid #fdba74',
                                                padding: '12px 16px',
                                                borderRadius: '10px',
                                                marginBottom: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}>
                                                <Icon icon="solar:wallet-money-bold-duotone" width="28" height="28" style={{ color: '#ea580c' }} />
                                                <div>
                                                    <strong style={{ color: '#9a3412', fontSize: '14px' }}>Cash / Price Wallet Transaction</strong>
                                                    <div style={{ fontSize: '13px', color: '#c2410c', marginTop: '2px' }}>
                                                        Wallet Amount Used: <strong>₹{getCashWalletAmount(viewInvoiceData).toFixed(2)}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* Meta row */}
                                        <div className="sr-inv-meta-grid">
                                            <div className="sr-inv-meta-card card-inv-no">
                                                <span className="sr-meta-label">Invoice No.</span>
                                                <span className="sr-meta-value accent">{viewInvoiceData.invoiceNo || "—"}</span>
                                            </div>
                                            <div className="sr-inv-meta-card card-date">
                                                <span className="sr-meta-label">Date &amp; Time</span>
                                                <span className="sr-meta-value">
                                                    {viewInvoiceData.createdAt
                                                        ? new Date(viewInvoiceData.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                                                        : "—"}
                                                </span>
                                            </div>
                                            <div className="sr-inv-meta-card">
                                                <span className="sr-meta-label">Status</span>
                                                <span className={`sr-meta-badge ${(viewInvoiceData.status || "Completed") === "Cancelled" ? "badge-cancelled" : "badge-completed"}`}>
                                                    {viewInvoiceData.status || "Completed"}
                                                </span>
                                            </div>
                                            <div className="sr-inv-meta-card">
                                                <span className="sr-meta-label">Payment Mode</span>
                                                <span className="sr-meta-value">
                                                    {Array.isArray(viewInvoiceData.paymentMode)
                                                        ? viewInvoiceData.paymentMode.map(p => p.method).join(" + ")
                                                        : (viewInvoiceData.paymentMode?.method || viewInvoiceData.paymentMode || "—")}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Customer + Store */}
                                        <div className="sr-inv-parties">
                                            <div className="sr-inv-party-box">
                                                <div className="sr-party-heading">
                                                    <Icon icon="mdi:account-outline" /> Customer
                                                </div>
                                                <p className="sr-party-name">
                                                    {viewInvoiceData.customerId?.name || "Walk-in Customer"}
                                                </p>
                                                {viewInvoiceData.customerId?.phoneNumber && (
                                                    <p className="sr-party-sub">
                                                        <Icon icon="mdi:phone-outline" /> {viewInvoiceData.customerId.phoneNumber}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="sr-inv-party-box">
                                                <div className="sr-party-heading">
                                                    <Icon icon="mdi:account-tie-outline" /> Billed By
                                                </div>
                                                <p className="sr-party-name">
                                                    {viewInvoiceData.billBy?.name || "—"}
                                                </p>
                                                {viewInvoiceData.billBy?.role && (
                                                    <p className="sr-party-sub">{viewInvoiceData.billBy.role}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Items Table */}
                                        {Array.isArray(viewInvoiceData.items) && viewInvoiceData.items.length > 0 && (
                                            <div className="sr-items-section">
                                                <h4 className="sr-section-title">
                                                    <Icon icon="mdi:list-box-outline" /> Items
                                                </h4>
                                                <div className="sr-items-table-wrap">
                                                    <table className="sr-items-table">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Item</th>
                                                                <th>Staff</th>
                                                                <th>Type</th>
                                                                <th>Qty</th>
                                                                <th>Rate</th>
                                                                <th>Discount</th>
                                                                <th>Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {viewInvoiceData.items.map((item, idx) => {
                                                                const name = item.serviceId?.serviceName
                                                                    || item.productId?.title
                                                                    || item.subscriptionId?.subscriptionName
                                                                    || item.productName
                                                                    || item.serviceName
                                                                    || item.subscriptionName
                                                                    || "Item";
                                                                const qty = Number(item.qty || 1);
                                                                const taxable = Number(item.taxableAmount || (Number(item.price || 0) * qty));
                                                                const rate = qty > 0 ? taxable / qty : 0;
                                                                const disc = Number(item.manualDiscount || item.itemDiscount || item.discount || item.membershipDiscount || 0);
                                                                const category = item.category || (item.serviceId ? "Service" : item.productId ? "Product" : item.subscriptionId ? "Membership" : "—");
                                                                return (
                                                                    <tr key={idx}>
                                                                        <td>{idx + 1}</td>
                                                                        <td className="item-name-cell">{name}</td>
                                                                        <td>
                                                                            {(() => {
                                                                                if (Array.isArray(item.employeeIds) && item.employeeIds.length > 0) {
                                                                                    return item.employeeIds.map(emp => {
                                                                                        if (typeof emp === 'object' && emp !== null) {
                                                                                            return emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.username || emp.employeeName;
                                                                                        }
                                                                                        return emp;
                                                                                    }).filter(Boolean).join(", ");
                                                                                }
                                                                                return item.employeeId?.name || "—";
                                                                            })()}
                                                                        </td>
                                                                        <td><span className={`type-badge type-${category.toLowerCase()}`}>{category}</span></td>
                                                                        <td className="text-center">{qty}</td>
                                                                        <td className="text-right">{rate.toFixed(2)}</td>
                                                                        <td className="text-right">{disc.toFixed(2)}</td>
                                                                        <td className="text-right fw-bold">{taxable.toFixed(2)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Billing Summary */}
                                        <div className="sr-billing-summary">
                                            <div className="sr-payment-info">
                                                <h4 className="sr-section-title">
                                                    <Icon icon="mdi:credit-card-outline" /> Payment
                                                </h4>
                                                {isServiceWalletBill(viewInvoiceData) && (
                                                    <div className="sr-pay-row" style={{ color: '#7e22ce', fontWeight: '600' }}>
                                                        <span>⭐ Service Wallet</span>
                                                        <span className="fw-bold">Service Redeemed</span>
                                                    </div>
                                                )}
                                                {isCashWalletBill(viewInvoiceData) && (
                                                    <div className="sr-pay-row" style={{ color: '#c2410c', fontWeight: '600' }}>
                                                        <span>💰 Cash / Price Wallet</span>
                                                        <span className="fw-bold">₹{getCashWalletAmount(viewInvoiceData).toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {Array.isArray(viewInvoiceData.paymentMode) ? (
                                                    viewInvoiceData.paymentMode
                                                        .filter(p => p.method !== "WALLET" && p.method !== "CASH_WALLET" && p.method !== "SERVICE_WALLET")
                                                        .map((p, idx) => (
                                                            <div key={idx} className="sr-pay-row">
                                                                <span>{p.method}{p.cardNumber ? ` (**** ${p.cardNumber})` : ""}</span>
                                                                <span className="fw-bold">₹{Number(p.amount || 0).toFixed(2)}</span>
                                                            </div>
                                                        ))
                                                ) : (!isCashWalletBill(viewInvoiceData) && !isServiceWalletBill(viewInvoiceData) && (
                                                    <div className="sr-pay-row">
                                                        <span>{viewInvoiceData.paymentMode?.method || viewInvoiceData.paymentMode || "Cash"}</span>
                                                        <span className="fw-bold">₹{Number(viewInvoiceData.paymentMode?.amount || viewInvoiceData.grandTotal || 0).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="sr-totals-info">
                                                <h4 className="sr-section-title">
                                                    <Icon icon="mdi:receipt-outline" /> Bill Summary
                                                </h4>
                                                {(() => {
                                                    const itemDiscountTotal = viewInvoiceData.items?.reduce((sum, item) => sum + Number(item.manualDiscount || item.itemDiscount || item.discount || 0), 0) || 0;
                                                    const membershipDiscount = Number(viewInvoiceData.membershipDiscount || viewInvoiceData.membershipDiscountAmount || 0);
                                                    const overallDiscount = Number(viewInvoiceData.overallDiscount || viewInvoiceData.overallDiscountAmount || 0);
                                                    const couponDiscount = Number(viewInvoiceData.couponDiscount || viewInvoiceData.couponDiscountAmount || 0);
                                                    const totalDiscounts = itemDiscountTotal + membershipDiscount + overallDiscount + couponDiscount;
                                                    const totalGST = Number(viewInvoiceData.totalGST || 0);
                                                    const roundOff = Number(viewInvoiceData.roundOff || 0);
                                                    const grandTotal = Number(viewInvoiceData.grandTotal || viewInvoiceData.finalAmount || viewInvoiceData.totalAmount || 0);
                                                    const netAmount = grandTotal - totalGST - roundOff;
                                                    const miscellaneousCharges = Number(viewInvoiceData.miscellaneousCharges || 0);
                                                    const subTotal = netAmount + totalDiscounts - miscellaneousCharges;
                                                    const tenderAmount = Array.isArray(viewInvoiceData.paymentMode)
                                                        ? viewInvoiceData.paymentMode.reduce((s, p) => s + Number(p.amount || 0), 0)
                                                        : Number(viewInvoiceData.paymentMode?.amount || grandTotal);
                                                    const changeAmount = Math.max(0, tenderAmount - grandTotal);
                                                    return (
                                                        <>
                                                            <div className="sr-total-row"><span>Subtotal</span><span>{subTotal.toFixed(2)}</span></div>
                                                            {miscellaneousCharges > 0 && <div className="sr-total-row"><span>Misc Charges</span><span>{miscellaneousCharges.toFixed(2)}</span></div>}
                                                            {itemDiscountTotal > 0 && <div className="sr-total-row"><span>Item Discount</span><span className="text-red">-{itemDiscountTotal.toFixed(2)}</span></div>}
                                                            {membershipDiscount > 0 && <div className="sr-total-row"><span>Membership Discount</span><span className="text-red">-{membershipDiscount.toFixed(2)}</span></div>}
                                                            {couponDiscount > 0 && <div className="sr-total-row"><span>Coupon Discount</span><span className="text-red">-{couponDiscount.toFixed(2)}</span></div>}
                                                            {overallDiscount > 0 && <div className="sr-total-row"><span>Overall Discount</span><span className="text-red">-{overallDiscount.toFixed(2)}</span></div>}
                                                            {totalGST > 0 && <div className="sr-total-row"><span>GST</span><span>{totalGST.toFixed(2)}</span></div>}
                                                            {roundOff !== 0 && <div className="sr-total-row"><span>Round Off</span><span>{roundOff.toFixed(2)}</span></div>}
                                                            <div className="sr-total-row grand"><span>Grand Total</span><span>{grandTotal.toFixed(2)}</span></div>
                                                            {isCashWalletBill(viewInvoiceData) && (
                                                                <div className="sr-total-row" style={{ color: '#c2410c', fontWeight: '600' }}>
                                                                    <span>Cash Wallet Paid</span>
                                                                    <span>-₹{getCashWalletAmount(viewInvoiceData).toFixed(2)}</span>
                                                                </div>
                                                            )}
                                                            {tenderAmount > 0 && (
                                                                <>
                                                                    <div className="sr-total-row"><span>Tender Amount</span><span>{tenderAmount.toFixed(2)}</span></div>
                                                                    <div className="sr-total-row"><span>Change Amount</span><span>{changeAmount.toFixed(2)}</span></div>
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="sr-modal-loading"><p>No invoice data available.</p></div>
                                )
                            )}

                            {/* MEMBERSHIP-WISE aggregated details */}
                            {activeTab === "membership-wise" && viewAggData && (
                                <div className="sr-agg-detail">
                                    <div className="sr-agg-hero">
                                        <div className="sr-agg-icon mem">
                                            <Icon icon="mdi:card-membership" />
                                        </div>
                                        <div>
                                            <p className="sr-agg-name">{viewAggData.name || "—"}</p>
                                            <p className="sr-agg-sub">Membership Plan</p>
                                        </div>
                                    </div>
                                    <div className="sr-agg-stats">
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:counter" className="stat-icon" />
                                            <span className="stat-label">Total Sold</span>
                                            <span className="stat-value">{viewAggData.count || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Total Revenue</span>
                                            <span className="stat-value">{(viewAggData.amount)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:chart-line" className="stat-icon" />
                                            <span className="stat-label">Avg. Price</span>
                                            <span className="stat-value">
                                                {viewAggData.count > 0 ? (viewAggData.amount / viewAggData.count) : "0"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SERVICE-WISE / PRODUCT-WISE aggregated details */}
                            {(activeTab === "service-wise" || activeTab === "product-wise") && viewAggData && (
                                <div className="sr-agg-detail">
                                    <div className="sr-agg-hero">
                                        <div className={`sr-agg-icon ${activeTab === "service-wise" ? "svc" : "prod"}`}>
                                            <Icon icon={activeTab === "service-wise" ? "mdi:scissors-cutting" : "mdi:package-variant-closed"} />
                                        </div>
                                        <div>
                                            <p className="sr-agg-name">
                                                {viewAggData.serviceName || viewAggData.productName || "—"}
                                            </p>
                                            <p className="sr-agg-sub">
                                                {activeTab === "service-wise" ? "Service" : "Product"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="sr-agg-stats">
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:counter" className="stat-icon" />
                                            <span className="stat-label">Total Quantity</span>
                                            <span className="stat-value">{viewAggData.totalQuantity || 0}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:currency-inr" className="stat-icon" />
                                            <span className="stat-label">Total Revenue</span>
                                            <span className="stat-value">{(viewAggData.totalAmount)}</span>
                                        </div>
                                        <div className="sr-agg-stat-card">
                                            <Icon icon="mdi:chart-line" className="stat-icon" />
                                            <span className="stat-label">Avg. Price / Unit</span>
                                            <span className="stat-value">
                                                {viewAggData.totalQuantity > 0
                                                    ? (viewAggData.totalAmount / viewAggData.totalQuantity)
                                                    : "0"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="sr-modal-footer">
                            <button className="sr-modal-btn-close" onClick={closeViewModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesReport;
